import type { Priority, Task, TaskFilters, TaskSort, TaskView, TaskViewInput } from "@hq/contracts";
import { TaskView as TaskViewSchema } from "@hq/contracts";
import { addDays, calendarDateOf, type NowContext, todayOf } from "./time";

const PRIORITY_RANK: Record<Priority, number> = { high: 0, medium: 1, low: 2 };

export function defineView(input: TaskViewInput): TaskView {
  return TaskViewSchema.parse(input);
}

function matchesFilters(task: Task, f: TaskFilters, ctx: NowContext, today: string): boolean {
  if (task.deletedAt) return false;
  const when =
    task.plannedDate ??
    (task.scheduledAt ? calendarDateOf(task.scheduledAt, ctx.timeZone) : undefined);
  if (f.sourceNoteId && task.sourceNoteId !== f.sourceNoteId) return false;
  if (
    f.attention === "inbox" &&
    (task.triaged || task.projectId || task.sourceNoteId || when || task.dueDate)
  )
    return false;
  if (f.attention === "anytime" && when && when > today) return false;
  if (
    f.attention === "upcoming" &&
    !(when && when > today) &&
    !(task.dueDate && task.dueDate > today)
  )
    return false;
  if (f.status && !f.status.includes(task.status)) return false;
  if (f.projectIds) {
    const pid = task.projectId ?? null;
    if (!f.projectIds.includes(pid)) return false;
  }
  if (f.priority && (task.priority === undefined || !f.priority.includes(task.priority)))
    return false;
  if (f.evening !== undefined && !!task.evening !== f.evening) return false;
  if (f.due) {
    const d = task.dueDate;
    switch (f.due.type) {
      case "today":
        if (d !== today) return false;
        break;
      case "overdue":
        if (d === undefined || d >= today) return false;
        break;
      case "upcoming":
        if (d === undefined || d < today || d > addDays(today, f.due.days)) return false;
        break;
      case "range":
        if (d === undefined || d < f.due.from || d > f.due.to) return false;
        break;
      case "none":
        if (d !== undefined) return false;
        break;
    }
  }
  if (f.scheduled) {
    const s =
      task.scheduledAt === undefined ? undefined : calendarDateOf(task.scheduledAt, ctx.timeZone);
    switch (f.scheduled.type) {
      case "today":
        if (s !== today) return false;
        break;
      case "range":
        if (s === undefined || s < f.scheduled.from || s > f.scheduled.to) return false;
        break;
      case "none":
        if (s !== undefined) return false;
        break;
    }
  }
  if (f.completedWithinDays !== undefined) {
    if (!task.completedAt) return false;
    const since = addDays(today, -f.completedWithinDays);
    if (calendarDateOf(task.completedAt, ctx.timeZone) < since) return false;
  }
  if (f.tags && f.tags.length > 0) {
    const wanted = f.tags.map((t) => t.toLowerCase());
    if (!(task.tags ?? []).some((t) => wanted.includes(t.toLowerCase()))) return false;
  }
  if (f.text) {
    const q = f.text.toLowerCase();
    if (
      !task.title.toLowerCase().includes(q) &&
      !(task.description ?? "").toLowerCase().includes(q)
    )
      return false;
  }
  return true;
}

function cmpOptional(a: string | number | undefined, b: string | number | undefined): number {
  // Missing values sort last regardless of direction.
  if (a === undefined && b === undefined) return 0;
  if (a === undefined) return 1;
  if (b === undefined) return -1;
  return a < b ? -1 : a > b ? 1 : 0;
}

export function compareTasks(a: Task, b: Task, sort: TaskSort): number {
  const dir = sort.direction === "asc" ? 1 : -1;
  let c = 0;
  switch (sort.by) {
    case "manual":
      c = a.sortOrder - b.sortOrder;
      break;
    case "dueDate":
      c = cmpOptional(a.dueDate, b.dueDate);
      if (a.dueDate === undefined || b.dueDate === undefined) return c; // missing last, ignore direction
      break;
    case "scheduledAt":
      c = cmpOptional(a.scheduledAt, b.scheduledAt);
      if (a.scheduledAt === undefined || b.scheduledAt === undefined) return c;
      break;
    case "priority":
      c = cmpOptional(
        a.priority && PRIORITY_RANK[a.priority],
        b.priority && PRIORITY_RANK[b.priority],
      );
      if (a.priority === undefined || b.priority === undefined) return c;
      break;
    case "completedAt":
      c = cmpOptional(a.completedAt, b.completedAt);
      if (a.completedAt === undefined || b.completedAt === undefined) return c;
      break;
    case "createdAt":
      c = cmpOptional(a.createdAt, b.createdAt);
      break;
    case "updatedAt":
      c = cmpOptional(a.updatedAt, b.updatedAt);
      break;
    case "title":
      c = a.title.localeCompare(b.title);
      break;
  }
  if (c === 0) c = a.sortOrder - b.sortOrder || a.createdAt.localeCompare(b.createdAt);
  return c * dir;
}

/** Filter and sort a coarse candidate set in memory. Repositories never implement this. */
export function applyTaskView(tasks: readonly Task[], view: TaskView, ctx: NowContext): Task[] {
  const today = todayOf(ctx);
  const filters: TaskFilters = view.display.showCompleted
    ? view.filters
    : { ...view.filters, status: view.filters.status ?? ["open", "someday"] };
  return tasks
    .filter((t) => matchesFilters(t, filters, ctx, today))
    .sort((a, b) => compareTasks(a, b, view.sort));
}

export type TaskGroup = { key: string; label: string; tasks: Task[] };

export type GroupContext = {
  projectNames?: ReadonlyMap<string, string>;
};

const PRIORITY_LABEL: Record<Priority, string> = { high: "High", medium: "Medium", low: "Low" };

/** Group already-filtered tasks. Group order follows first appearance except dates and priorities, which sort naturally. */
export function groupTasks(
  tasks: readonly Task[],
  view: TaskView,
  ctx: NowContext,
  gctx: GroupContext = {},
): TaskGroup[] {
  if (view.groupBy === "none") return [{ key: "all", label: "", tasks: [...tasks] }];
  const groups = new Map<string, TaskGroup>();
  const today = todayOf(ctx);
  const push = (key: string, label: string, t: Task) => {
    let g = groups.get(key);
    if (!g) {
      g = { key, label, tasks: [] };
      groups.set(key, g);
    }
    g.tasks.push(t);
  };
  for (const t of tasks) {
    switch (view.groupBy) {
      case "project":
        push(
          t.projectId ?? "unassigned",
          t.projectId ? (gctx.projectNames?.get(t.projectId) ?? "Project") : "No project",
          t,
        );
        break;
      case "dueDate":
        if (!t.dueDate) push("~none", "No date", t);
        else if (t.dueDate < today) push("0-overdue", "Overdue", t);
        else if (t.dueDate === today) push("1-today", "Today", t);
        else push(`2-${t.dueDate}`, t.dueDate, t);
        break;
      case "priority":
        push(
          t.priority ? `${PRIORITY_RANK[t.priority]}-${t.priority}` : "9-none",
          t.priority ? PRIORITY_LABEL[t.priority] : "No priority",
          t,
        );
        break;
      case "heading":
        push(t.heading ?? "~none", t.heading ?? "", t);
        break;
      case "plannedDate": {
        const when =
          t.plannedDate ??
          (t.scheduledAt ? calendarDateOf(t.scheduledAt, ctx.timeZone) : undefined);
        const dates = [when, t.dueDate]
          .filter((date): date is string => !!date && date > today)
          .sort();
        const date = dates[0];
        push(date ?? "~none", date ?? "Anytime", t);
        break;
      }
      case "status":
        push(
          t.status,
          t.status === "open" ? "Open" : t.status === "someday" ? "Someday" : "Completed",
          t,
        );
        break;
    }
  }
  const out = [...groups.values()];
  if (view.groupBy === "dueDate" || view.groupBy === "plannedDate" || view.groupBy === "priority")
    out.sort((a, b) => a.key.localeCompare(b.key));
  return out;
}

/** Built-in views. Not persisted; they run through the same engine as saved views. */
export const builtInViews = {
  inbox: defineView({
    id: "inbox",
    name: "Inbox",
    filters: { attention: "inbox", status: ["open"] },
    sort: { by: "manual", direction: "asc" },
  }),
  anytime: defineView({
    id: "anytime",
    name: "Anytime",
    filters: { attention: "anytime", status: ["open"] },
    sort: { by: "manual", direction: "asc" },
    groupBy: "project",
  }),
  today: defineView({
    id: "today",
    name: "Today",
    filters: { status: ["open"], due: { type: "today" } },
    sort: { by: "priority", direction: "asc" },
  }),
  scheduledToday: defineView({
    id: "scheduled-today",
    name: "Scheduled today",
    filters: { status: ["open"], scheduled: { type: "today" } },
    sort: { by: "scheduledAt", direction: "asc" },
  }),
  overdue: defineView({
    id: "overdue",
    name: "Overdue",
    filters: { status: ["open"], due: { type: "overdue" } },
    sort: { by: "dueDate", direction: "asc" },
  }),
  upcoming: defineView({
    id: "upcoming",
    name: "Upcoming",
    filters: { status: ["open"], attention: "upcoming" },
    sort: { by: "dueDate", direction: "asc" },
    groupBy: "plannedDate",
  }),
  someday: defineView({
    id: "someday",
    name: "Someday",
    filters: { status: ["someday"] },
    sort: { by: "updatedAt", direction: "desc" },
  }),
  completed: defineView({
    id: "completed",
    name: "Completed",
    filters: { status: ["completed"] },
    sort: { by: "completedAt", direction: "desc" },
    display: { showCompleted: true, density: "compact", showProject: true },
  }),
} as const satisfies Record<string, TaskView>;

export type BuiltInViewId = keyof typeof builtInViews;

export function projectView(projectId: string, name: string): TaskView {
  return defineView({
    id: `project:${projectId}`,
    name,
    filters: { projectIds: [projectId], status: ["open", "someday"] },
    sort: { by: "manual", direction: "asc" },
    groupBy: "heading",
  });
}

/** Today view composition: due today, overdue, and scheduled today, deduplicated. */
export function todayTasks(
  tasks: readonly Task[],
  ctx: NowContext,
): { overdue: Task[]; today: Task[]; evening: Task[] } {
  const overdue = applyTaskView(tasks, builtInViews.overdue, ctx);
  const seen = new Set(overdue.map((t) => t.id));
  const dueToday = applyTaskView(tasks, builtInViews.today, ctx);
  const scheduled = applyTaskView(tasks, builtInViews.scheduledToday, ctx);
  const merged: Task[] = [];
  const planned = tasks.filter(
    (t) =>
      !t.deletedAt &&
      t.status === "open" &&
      ((t.plannedDate && t.plannedDate <= todayOf(ctx)) ||
        (t.scheduledAt && calendarDateOf(t.scheduledAt, ctx.timeZone) < todayOf(ctx))),
  );
  for (const t of [...dueToday, ...scheduled, ...planned]) {
    if (!seen.has(t.id)) {
      seen.add(t.id);
      merged.push(t);
    }
  }
  return {
    overdue,
    today: merged.filter((t) => !t.evening),
    evening: merged.filter((t) => !!t.evening),
  };
}
